/**
 * Support Chat Widget
 *
 * Floating support chat panel for human-to-human support.
 * Uses Slack integration for support team communication.
 *
 * @module
 * @see apps/web/src/hooks/useSupportChat.ts - Data hook
 * @see packages/ui/src/components/FloatingChatPanel.tsx - Panel component
 */

import React from 'react';
import { FloatingChatPanel, ChatMessage, ChatMessageList, ChatComposer, Text, Tooltip, TooltipTrigger, TagBadge, Markdown } from '@chatsdet/ui';
import { Chat, Check, ArrowLeft, Plus, Users, Paperclip, X } from '@phosphor-icons/react';
import {
  useSupportChat,
  type SupportMessage,
  type SupportConversation,
} from '../../../hooks/useSupportChat';

// ============================================================================
// Types
// ============================================================================

interface SupportChatWidgetProps {
  /** Whether the panel is open */
  isOpen: boolean;
  /** Called when panel should close */
  onClose: () => void;
  /** Custom class name */
  className?: string;
  /**
   * When set, the widget auto-opens this conversation's thread (used by the
   * `?support=<conversationId>` deep-link from email / notifications).
   */
  initialConversationId?: string;
}

// ============================================================================
// Sub-components
// ============================================================================

/** Max images per message — mirrors the API's MAX_SUPPORT_FILES. */
const MAX_ATTACHMENTS = 10;

interface ImageAttachmentsProps {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
}

/**
 * Image picker + thumbnail previews for the composer. Tracks a `File[]` in the
 * parent; the parent sends them as multipart form-data via useSupportChat.
 */
function ImageAttachments({ files, onChange, disabled }: ImageAttachmentsProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const urls = React.useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  React.useEffect(() => () => urls.forEach((u) => URL.revokeObjectURL(u)), [urls]);

  const addFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    onChange([...files, ...Array.from(list)].slice(0, MAX_ATTACHMENTS));
  };
  const removeFile = (index: number) => onChange(files.filter((_, i) => i !== index));

  return (
    <div className="flex flex-col gap-2">
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {files.map((file, i) => (
            <div key={i} className="relative">
              <img
                src={urls[i]}
                alt={file.name}
                className="h-14 w-14 rounded-md border border-border object-cover"
              />
              <button
                type="button"
                onClick={() => removeFile(i)}
                aria-label={`Remove ${file.name}`}
                className="absolute -right-1.5 -top-1.5 rounded-full border border-border bg-background p-0.5 text-muted-foreground hover:text-foreground"
              >
                <X size={10} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = '';
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || files.length >= MAX_ATTACHMENTS}
        className="inline-flex w-fit items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
      >
        <Paperclip size={14} />
        Attach image
      </button>
    </div>
  );
}

interface ConversationListViewProps {
  conversations: SupportConversation[];
  onSelect: (id: string) => void;
  onNewConversation: () => void;
}

function ConversationListView({
  conversations,
  onSelect,
  onNewConversation,
}: ConversationListViewProps) {
  if (conversations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-center">
        <Chat size={48} className="text-muted-foreground/30 mb-4" />
        <Text size="sm" className="text-muted-foreground mb-4">
          No conversations yet
        </Text>
        <button
          onClick={onNewConversation}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm font-medium"
        >
          <Plus size={16} />
          Start a conversation
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b border-border">
        <button
          onClick={onNewConversation}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm font-medium"
        >
          <Plus size={16} />
          New conversation
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {conversations.map((conversation) => (
          <button
            key={conversation.id}
            onClick={() => onSelect(conversation.id)}
            className="w-full p-3 text-left border-b border-border hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <Text size="sm" className="font-medium text-foreground truncate">
                  {conversation.subject || 'Support Request'}
                </Text>
                <Text size="xs" className="text-muted-foreground mt-0.5">
                  {formatRelativeTime(conversation.updatedAt)}
                </Text>
              </div>
              {conversation.status === 'open' ? (
                <span className="shrink-0 px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-700 rounded">
                  Open
                </span>
              ) : (
                <span className="shrink-0 px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-600 rounded">
                  Resolved
                </span>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

interface ChatThreadViewProps {
  conversation: SupportConversation;
  messages: SupportMessage[];
  isLoading: boolean;
  isSending: boolean;
  onBack: () => void;
  onSend: (content: string, files: File[]) => Promise<void>;
  onResolve: () => Promise<void>;
}

function ChatThreadView({
  conversation,
  messages,
  isLoading,
  isSending,
  onBack,
  onSend,
  onResolve,
}: ChatThreadViewProps) {
  const [input, setInput] = React.useState('');
  const [files, setFiles] = React.useState<File[]>([]);

  const handleSubmit = async () => {
    const content = input.trim();
    if ((!content && files.length === 0) || isSending) return;
    const outgoing = files;
    setInput('');
    setFiles([]);
    await onSend(content, outgoing);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Thread header */}
      <div className="flex items-center gap-2 p-3 border-b border-border shrink-0">
        <button
          onClick={onBack}
          className="p-1 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Back to conversations"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <Text size="sm" className="font-medium text-foreground truncate">
            {conversation.subject || 'Support Request'}
          </Text>
        </div>
        {conversation.status === 'open' && (
          <TooltipTrigger>
            <button
              onClick={onResolve}
              className="p-1 text-muted-foreground hover:text-green-600 transition-colors"
              aria-label="Mark as resolved"
            >
              <Check size={18} />
            </button>
            <Tooltip>Mark resolved</Tooltip>
          </TooltipTrigger>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            Loading messages...
          </div>
        ) : (
          <ChatMessageList className="p-3" isStreaming={isSending}>
            {messages.map((message) => (
              <ChatMessage
                key={message.id}
                role={message.senderType === 'user' ? 'user' : 'assistant'}
                author={message.senderType === 'user' ? 'You' : message.senderName || 'Support'}
                timestamp={formatTime(message.createdAt)}
              >
                <Markdown className="text-sm" compact>
                  {message.content}
                </Markdown>
                {message.attachments && message.attachments.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {message.attachments.map((att, i) =>
                      att.type === 'image' ? (
                        <img
                          key={i}
                          src={att.url}
                          alt={att.name ?? 'Image'}
                          className="max-w-full rounded-md border border-border"
                        />
                      ) : (
                        <a
                          key={i}
                          href={att.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                        >
                          {att.name ?? 'Download file'}
                        </a>
                      )
                    )}
                  </div>
                )}
              </ChatMessage>
            ))}
          </ChatMessageList>
        )}
      </div>

      {/* Input - only show for open conversations */}
      {conversation.status === 'open' && (
        <div className="border-t border-border shrink-0">
          <div className="px-3 pt-3">
            <ImageAttachments files={files} onChange={setFiles} disabled={isSending} />
          </div>
          <ChatComposer
            value={input}
            onChange={setInput}
            onSubmit={handleSubmit}
            isSending={isSending}
            allowEmptySubmit={files.length > 0}
            placeholder="Type a message..."
            submitLabel="Send"
            className="border-none rounded-none"
          />
        </div>
      )}
    </div>
  );
}

interface NewConversationViewProps {
  isSending: boolean;
  onBack: () => void;
  onSubmit: (subject: string | undefined, message: string, files: File[]) => Promise<void>;
}

function NewConversationView({ isSending, onBack, onSubmit }: NewConversationViewProps) {
  const [subject, setSubject] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [files, setFiles] = React.useState<File[]>([]);

  const handleSubmit = async () => {
    if ((!message.trim() && files.length === 0) || isSending) return;
    await onSubmit(subject.trim() || undefined, message.trim(), files);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 p-3 border-b border-border shrink-0">
        <button
          onClick={onBack}
          className="p-1 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Back to conversations"
        >
          <ArrowLeft size={18} />
        </button>
        <Text size="sm" className="font-medium text-foreground">
          New conversation
        </Text>
      </div>

      {/* Form */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
              Subject (optional)
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="What do you need help with?"
              className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
              Message
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Describe your issue or question..."
              rows={6}
              className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
            />
          </div>
          <ImageAttachments files={files} onChange={setFiles} disabled={isSending} />
        </div>
      </div>

      {/* Submit */}
      <div className="p-4 border-t border-border shrink-0">
        <button
          onClick={handleSubmit}
          disabled={(!message.trim() && files.length === 0) || isSending}
          className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSending ? 'Sending...' : 'Start conversation'}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function formatTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// ============================================================================
// Main Component
// ============================================================================

type ViewState = 'list' | 'thread' | 'new';

/**
 * Support chat panel controlled by parent component.
 * Allows users to start conversations and receive real-time replies.
 */
export function SupportChatWidget({
  isOpen,
  onClose,
  className,
  initialConversationId,
}: SupportChatWidgetProps) {
  const [viewState, setViewState] = React.useState<ViewState>('list');

  const {
    activeConversation,
    conversations,
    messages,
    isLoadingMessages,
    isSending,
    startConversation,
    sendMessage,
    selectConversation,
    resolveConversation,
  } = useSupportChat();

  // Deep-link: when opened to a specific conversation (email / notification
  // link), jump straight into that thread. Keyed on the id so it fires once
  // per distinct deep-link, not on every manual open.
  React.useEffect(() => {
    if (!initialConversationId) return;
    selectConversation(initialConversationId);
    setViewState('thread');
  }, [initialConversationId, selectConversation]);

  const handleClose = () => {
    setViewState('list');
    selectConversation(null);
    onClose();
  };

  const handleSelectConversation = (id: string) => {
    selectConversation(id);
    setViewState('thread');
  };

  const handleNewConversation = () => {
    setViewState('new');
  };

  const handleBackToList = () => {
    selectConversation(null);
    setViewState('list');
  };

  const handleStartConversation = async (
    subject: string | undefined,
    initialMessage: string,
    files: File[]
  ) => {
    await startConversation(subject, initialMessage, files);
    setViewState('thread');
  };

  // Render content based on view state
  const renderContent = () => {
    if (viewState === 'new') {
      return (
        <NewConversationView
          isSending={isSending}
          onBack={handleBackToList}
          onSubmit={handleStartConversation}
        />
      );
    }

    if (viewState === 'thread' && activeConversation) {
      return (
        <ChatThreadView
          conversation={activeConversation}
          messages={messages}
          isLoading={isLoadingMessages}
          isSending={isSending}
          onBack={handleBackToList}
          onSend={sendMessage}
          onResolve={resolveConversation}
        />
      );
    }

    return (
      <ConversationListView
        conversations={conversations}
        onSelect={handleSelectConversation}
        onNewConversation={handleNewConversation}
      />
    );
  };

  // Badge for human support indicator
  const supportBadge = (
    <TagBadge
      name="Human support"
      color="green"
      size="sm"
      icon={<Users size={10} weight="fill" />}
    />
  );

  return (
    <FloatingChatPanel
      isOpen={isOpen}
      title="Support"
      badge={supportBadge}
      onClose={handleClose}
      width={380}
      className={className}
    >
      {renderContent()}
    </FloatingChatPanel>
  );
}
